import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, money, useQuery } from '../api.js';
import { Drawer, ErrorNote, Pill, Spinner, Stat, useToast } from '../ui.js';
import { AvatarPicker } from '../avatar.js';
import { useSession } from '../session.js';
import { RecordsOnFile } from './Records.js';
import { History } from './History.js';
import type { SyllabusRow } from '../../../shared/types.js';

interface Data {
  student: {
    id: number; userId: number; avatarUpdatedAt: number | null;
    name: string; initials: string; email: string;
    courseName: string; regulation: string; enrolledOn: string;
    primaryInstructorName: string; stage: number;
    balanceCents: number; creditCents: number;
  };
  totals: { total: number; dual: number; solo: number; ground: number; night: number; instrument: number };
  syllabus: SyllabusRow[];
  endorsements: { id: number; label: string; reference: string; issuedOn: string; expiresOn: string | null; issuedByName: string; expired: boolean }[];
  upcoming: { id: number; startsAt: number; lessonLabel: string; aircraftTail: string; status: string }[];
  showGrades: boolean;
  gates: boolean;
  canGrade: boolean;
  canApproveStage: boolean;
}

export function StudentRecord(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { role } = useSession();
  const { data, loading, error, reload } = useQuery<Data>(`/students/${id}`);
  const [grading, setGrading] = useState<SyllabusRow | null>(null);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const s = data.student;

  const approve = async (lessonId: number) => {
    try {
      await api.post(`/students/${id}/approve-stage`, { lessonId });
      toast.ok('Stage record approved', 'The next stage is released.');
      reload();
    } catch (err) {
      toast.error('Could not approve', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="split" style={{ padding: 12 }}>
      <div className="split__main">
        <div className="card" style={{ padding: 12, display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
          <AvatarPicker
            userId={s.userId}
            updatedAt={s.avatarUpdatedAt}
            initials={s.initials}
            label={s.name}
            size={56}
            canEdit={role === 'management'}
            onChanged={reload}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>{s.name}</div>
            <div className="small muted">
              Part {s.regulation} · {s.courseName} · enrolled {s.enrolledOn} · instructor {s.primaryInstructorName}
            </div>
            <div className="row" style={{ gap: 6, marginTop: 7 }}>
              <Pill tone="blue">Stage {s.stage}</Pill>
              {data.endorsements.filter((e) => !e.expired).slice(0, 2).map((e) => (
                <Pill key={e.id} tone="green">{e.label}</Pill>
              ))}
              {s.balanceCents > 0 && <Pill tone="red">{money(s.balanceCents)} outstanding</Pill>}
            </div>
          </div>
          <span className="row" style={{ gap: 8 }}>
            {/* Opens the inbox on a conversation with them. A notice is a
                different act and is sent from the inbox itself. */}
            <button className="btn" onClick={() => navigate(`?dm=${s.userId}`)}>Message</button>
            <button className="btn btn--primary" onClick={() => navigate(`/schedule?new=1`)}>
              Schedule next lesson
            </button>
          </span>
        </div>

        <div className="stat-grid">
          <Stat label="Total time" value={data.totals.total.toFixed(1)} />
          <Stat label="Dual" value={data.totals.dual.toFixed(1)} />
          <Stat label="Solo" value={data.totals.solo.toFixed(1)} />
          <Stat label="Instrument" value={data.totals.instrument.toFixed(1)} />
          <Stat label="Night" value={data.totals.night.toFixed(1)} />
        </div>

        <div className="card">
          <div className="card__head">
            <span className="card__title">Syllabus · {s.courseName}</span>
            <span className="card__meta">{data.gates ? 'Stage gates enforced' : 'Lesson order is advisory'}</span>
          </div>
          <div className="table__scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th><th>Lesson</th><th>Flown</th><th>Hrs</th><th>Grade</th>
                  <th>{data.gates ? 'Stage gate' : 'Progress'}</th><th />
                </tr>
              </thead>
              <tbody>
                {data.syllabus.map((row) => (
                  <tr key={row.lesson.id} style={
                    row.lesson.isRemedial ? { background: 'var(--tint-orange)' }
                    : row.lesson.isStageCheck ? { background: 'var(--fill-4)' } : undefined
                  }>
                    <td className="bold">{row.lesson.code}</td>
                    <td>
                      {row.lesson.name}
                      {row.lesson.isRemedial && (
                        <div className="small muted">Flown only if a lesson in this stage is unsatisfactory</div>
                      )}
                      {row.gateReason && <div className="small" style={{ color: 'var(--sys-red)' }}>{row.gateReason}</div>}
                    </td>
                    <td>{row.record?.flownOn ?? <span className="muted">—</span>}</td>
                    <td className="num">{row.record ? row.record.flightHours.toFixed(1) : '—'}</td>
                    <td>{row.record?.grade ?? <span className="muted">—</span>}</td>
                    <td><Pill>{row.state}</Pill></td>
                    <td className="num">
                      {data.canApproveStage && row.lesson.isStageCheck && row.record?.grade === 'S' && !row.record.approvedOn && (
                        <button className="btn btn--link" onClick={() => approve(row.lesson.id)}>Approve</button>
                      )}
                      {data.canGrade && !row.lesson.isStageCheck && (
                        <button className="btn btn--link" onClick={() => setGrading(row)}>
                          {row.record ? 'Amend' : 'Record'}
                        </button>
                      )}
                      {data.canGrade && row.lesson.isStageCheck && !row.record && (
                        <button className="btn btn--link" onClick={() => setGrading(row)}>Record</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {loading && <div className="small muted" style={{ padding: 8 }}>Refreshing…</div>}
      </div>

      <aside className="split__aside">
        <div className="card" style={{ marginBottom: 12 }}>
          {/* Endorsements, not paperwork: what an instructor has signed this
              student off to do. The documents the school holds a copy of are
              the card below. */}
          <div className="card__head"><span className="card__title">Currency &amp; endorsements</span></div>
          {data.endorsements.length === 0 && <div className="empty">Nothing on file.</div>}
          {data.endorsements.map((e) => (
            <div className="kv" key={e.id}>
              <span style={{ fontWeight: 400 }}>{e.label}</span>
              <span style={{ color: e.expired ? 'var(--conflict-c)' : 'var(--text-2)' }}>
                {e.expiresOn ? (e.expired ? `Expired ${e.expiresOn}` : `To ${e.expiresOn}`) : `On file ${e.issuedOn}`}
              </span>
            </div>
          ))}
          <div className="card__body small muted" style={{ textWrap: 'pretty' }}>
            Dates are reported from the records on file. Verify against the logbook before flight.
          </div>
        </div>

        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card__head"><span className="card__title">Documents on file</span></div>
          <RecordsOnFile userId={s.userId} emptyNote="Nothing is on file for this student." />
        </div>

        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card__head"><span className="card__title">History</span></div>
          <div style={{ padding: '8px 12px 4px' }}>
            <History
              compact
              entity="student"
              entityId={s.id}
              limit={8}
              emptyNote="Nothing recorded against this student yet."
            />
          </div>
        </div>

        <div className="card">
          <div className="card__head"><span className="card__title">Upcoming · {data.upcoming.length}</span></div>
          {data.upcoming.length === 0 && <div className="empty">Nothing booked.</div>}
          {data.upcoming.map((r) => (
            <div className="kv" key={r.id}>
              <span style={{ fontWeight: 400 }}>
                {new Date(r.startsAt).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                {' '}{new Date(r.startsAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
              <button className="btn btn--link" onClick={() => navigate(`/schedule?reservation=${r.id}`)}>
                {r.lessonLabel || r.aircraftTail}
              </button>
            </div>
          ))}
        </div>
      </aside>

      {grading && (
        <GradeDrawer
          studentId={Number(id)}
          row={grading}
          onClose={() => setGrading(null)}
          onSaved={() => { setGrading(null); reload(); }}
        />
      )}
    </div>
  );
}

function GradeDrawer({ studentId, row, onClose, onSaved }: {
  studentId: number; row: SyllabusRow; onClose: () => void; onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [grade, setGrade] = useState<'S' | 'U' | 'I'>(row.record?.grade ?? 'S');
  const [flightHours, setFlightHours] = useState(String(row.record?.flightHours ?? row.lesson.flightHours));
  const [groundHours, setGroundHours] = useState(String(row.record?.groundHours ?? row.lesson.groundHours));
  const [notes, setNotes] = useState(row.record?.notes ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/students/${studentId}/records`, {
        lessonId: row.lesson.id, grade,
        flightHours: Number(flightHours), groundHours: Number(groundHours),
        notes,
      });
      toast.ok('Training record saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      narrow
      title={`${row.lesson.code} · ${row.lesson.name}`}
      subtitle={row.lesson.isStageCheck ? 'Stage check — a chief instructor must approve it afterwards' : undefined}
      onClose={onClose}
      footer={<button className="btn btn--primary" disabled={busy} onClick={save}>Save record</button>}
    >
      <div className="card__body stack">
        <label className="field">
          <span>Grade</span>
          <select className="select" value={grade} onChange={(e) => setGrade(e.target.value as 'S' | 'U' | 'I')}>
            <option value="S">S · satisfactory</option>
            <option value="U">U · unsatisfactory, repeat the lesson</option>
            <option value="I">I · incomplete</option>
          </select>
        </label>
        <label className="field">
          <span>Flight hours</span>
          <input className="input" inputMode="decimal" value={flightHours} onChange={(e) => setFlightHours(e.target.value)} />
        </label>
        <label className="field">
          <span>Ground hours</span>
          <input className="input" inputMode="decimal" value={groundHours} onChange={(e) => setGroundHours(e.target.value)} />
        </label>
        <label className="field">
          <span>Notes</span>
          <textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <span className="small muted">
          Closing a flight out from the schedule writes this record automatically. Use this to record
          ground lessons or to correct what was entered.
        </span>
      </div>
    </Drawer>
  );
}
